/**
 * The chemistry lab's saved setup: the bench as it was set out — glassware,
 * stands, burners, what is in each vessel — and, for a teacher's own guided
 * experiment, the steps.
 *
 * A setup, not a session. What reacted, the notebook, the pH paper and any gas
 * that came down a tube are what HAPPENED on the bench, and opening a setup
 * makes them happen again in a fresh, metered session. See
 * docs/FeatureDesigns/lab-setups/lab-setups-target.md §6.4.
 *
 * The steps are the curated catalogue's own `GuidedStep`s, so the bench's
 * guided runner plays a teacher's experiment exactly as it plays ours, and the
 * checks here are the seed script's: every reagent on the shelf, every vessel a
 * step names on the bench, and no more reactions than one session allows.
 */
import { findSubstance } from '../substanceCatalog';
import { VESSEL_TYPES, VESSEL_LABELS, type VesselType } from '../vesselTypes';
import type { GuidedStep } from '../guidedExperimentCatalog';

export interface ChemistrySetupContent {
    substanceId: string;
    qty: number;
    /** The strength it went in at, on the bench with amounts, when one was chosen. */
    concentrationM?: number;
}

export interface ChemistrySetupVessel {
    vesselType: VesselType;
    /** shared-ui's StandType. Checked as a known stand here; a stand the
     *  vessel cannot use is put right by the bench, as a reshape would. */
    standType: string;
    burnerType: 'none' | 'bunsen' | 'spirit-lamp';
    temperatureC: number;
    /** What the steps call it — "the lime water". Also drawn on the vessel. */
    label?: string;
    contents: ChemistrySetupContent[];
    /** The reagent the glass rod resting in it carries. */
    rod?: string;
    /** A delivery tube from this vessel into that one, by index. */
    pipeTo?: number;
}

export interface ChemistrySetupPayload {
    vessels: ChemistrySetupVessel[];
    /** Present and non-empty: a guided experiment, played step by step. */
    steps?: GuidedStep[];
    /** Said when a run is finished — the one thing it exists to show. */
    conclusion?: string;
}

/** What one session of the bench allows — functions' CHEMISTRY_LAB_MAX_STEPS,
 *  which a test holds this to. Shared code cannot import from functions. */
export const CHEMISTRY_SESSION_REACTIONS = 10;
export const CHEMISTRY_SETUP_MAX_VESSELS = 8;
export const CHEMISTRY_SETUP_MAX_STEPS = 40;

const STANDS = ['none', 'tripod', 'retort', 'tube-rack', 'multi-level'];
const BURNERS = ['none', 'bunsen', 'spirit-lamp'];
/** shared-ui's temperature.ts: the burner's range, and where "heated" starts. */
const TEMP_MIN_C = 0;
const TEMP_MAX_C = 200;
const WARM_C = 40;

const TEXT_MAX = 300;
const LONG_TEXT_MAX = 1000;

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const shortText = (v: unknown, max: number): boolean => typeof v === 'string' && v.trim().length > 0 && v.length <= max;
const optText = (v: unknown, max: number): boolean => v === undefined || (typeof v === 'string' && v.length <= max);

/** A reagent a teacher can have set out: on the shelf, which a gas never is —
 *  a gas reaches a vessel down a tube, and opening the setup sends it again. */
function shelfError(id: unknown, at: string): string | null {
    if (typeof id !== 'string') return `${at} names no reagent`;
    const s = findSubstance(id);
    if (!s) return `${at} is "${id}", which is not on the shelf`;
    if (s.category === 'gas') return `${at} is a gas, which reaches a vessel through a delivery tube`;
    return null;
}

function vesselErrors(v: unknown, i: number, count: number): string[] {
    const at = `vessels[${i}]`;
    if (!isObject(v)) return [`${at} is not a vessel`];
    const e: string[] = [];
    if (!(VESSEL_TYPES as readonly string[]).includes(v.vesselType as string)) e.push(`${at} is a "${String(v.vesselType)}", which the bench has no glass for`);
    if (!STANDS.includes(v.standType as string)) e.push(`${at} stands on a "${String(v.standType)}", which the bench has no stand for`);
    if (!BURNERS.includes(v.burnerType as string)) e.push(`${at} has a "${String(v.burnerType)}" under it, which is not a burner`);
    if (!finite(v.temperatureC) || v.temperatureC < TEMP_MIN_C || v.temperatureC > TEMP_MAX_C) e.push(`${at} is at a temperature the burner cannot set`);
    if (v.label !== undefined && !shortText(v.label, 40)) e.push(`${at} has a name that is not short text`);
    if (!Array.isArray(v.contents)) e.push(`${at} has no contents list`);
    else {
        if (v.contents.length > 12) e.push(`${at} holds ${v.contents.length} reagents; at most 12`);
        v.contents.forEach((c, j) => {
            const cat = `${at}.contents[${j}]`;
            if (!isObject(c)) { e.push(`${cat} is not a reagent`); return; }
            const bad = shelfError(c.substanceId, cat);
            if (bad) e.push(bad);
            if (!finite(c.qty) || c.qty < 0 || c.qty > 10_000) e.push(`${cat} has an amount that is not a number`);
            if (c.concentrationM !== undefined && (!finite(c.concentrationM) || c.concentrationM <= 0 || c.concentrationM > 50)) e.push(`${cat} has a strength that is not a molarity`);
        });
        const ids = (v.contents as { substanceId?: unknown }[]).map(c => c?.substanceId);
        if (new Set(ids).size !== ids.length) e.push(`${at} lists a reagent twice`);
    }
    if (v.rod !== undefined) {
        const bad = shelfError(v.rod, `${at}.rod`);
        if (bad) e.push(bad);
    }
    if (v.pipeTo !== undefined && (!Number.isInteger(v.pipeTo) || (v.pipeTo as number) < 0 || (v.pipeTo as number) >= count || v.pipeTo === i)) {
        e.push(`${at} has a delivery tube to a vessel that is not on the bench`);
    }
    return e;
}

const EXPECT_KINDS = ['add-substance', 'dip-test', 'dip-rod', 'attach-burner', 'heat', 'connect-pipe', 'swirl', 'observe'];

function stepErrors(s: unknown, i: number, vessels: number): string[] {
    const at = `steps[${i}]`;
    if (!isObject(s)) return [`${at} is not a step`];
    const e: string[] = [];
    if (!shortText(s.instruction, TEXT_MAX)) e.push(`${at} needs an instruction of at most ${TEXT_MAX} characters`);
    if (!optText(s.correction, TEXT_MAX)) e.push(`${at} has a correction longer than ${TEXT_MAX} characters`);
    if (!optText(s.observe, TEXT_MAX)) e.push(`${at} has a "look for" longer than ${TEXT_MAX} characters`);
    if (!Number.isInteger(s.container) || (s.container as number) < 0 || (s.container as number) >= vessels) e.push(`${at} acts on a vessel that is not on the bench`);
    const x = s.expect;
    if (!isObject(x) || !EXPECT_KINDS.includes(x.kind as string)) {
        // answer-quantity included: nothing on the bench records one, so a
        // teacher's experiment never asks for a number.
        e.push(`${at} asks for "${isObject(x) ? String(x.kind) : '?'}", which a teacher's experiment cannot ask for`);
        return e;
    }
    if (x.kind === 'add-substance') {
        const bad = shelfError(x.substanceId, `${at}'s reagent`);
        if (bad) e.push(bad);
        if (x.qty !== undefined && (!finite(x.qty) || x.qty <= 0)) e.push(`${at} wants an amount that is not a number`);
    }
    if (x.kind === 'dip-rod') {
        const bad = shelfError(x.substanceId, `${at}'s rod reagent`);
        if (bad) e.push(bad);
    }
    if (x.kind === 'heat' && x.toBand !== 'heated' && x.toBand !== 'boiling') e.push(`${at} heats to neither hot nor boiling`);
    if (x.kind === 'connect-pipe' && (!Number.isInteger(x.toContainer) || (x.toContainer as number) < 0
        || (x.toContainer as number) >= vessels || x.toContainer === s.container)) {
        e.push(`${at} runs a tube to a vessel that is not on the bench`);
    }
    return e;
}

/** Does this vessel need the chemistry asked when the setup opens? One reagent
 *  at room temperature is answered from the catalogue and costs nothing. */
function mixesOnOpening(v: ChemistrySetupVessel): boolean {
    return v.contents.length > 1 || (v.contents.length === 1 && v.temperatureC >= WARM_C);
}

/**
 * How many reactions a run of this setup can ask for: what opening it costs,
 * and every step that reaches the chemistry. Counted as the seed script counts
 * a catalogue experiment's (`mixStepCount`) — generously, since a pour into an
 * empty vessel is answered for free — so a run never stops short of its end.
 */
export function chemistryReactionCount(payload: ChemistrySetupPayload): number {
    const opening = payload.vessels.reduce((n, v) => n
        + (mixesOnOpening(v) ? 1 : 0)
        // Its gas goes down the tube, and the vessel it reaches is asked again.
        + (v.pipeTo !== undefined && mixesOnOpening(v) ? 1 : 0), 0);
    const steps = (payload.steps ?? []).filter(s => (
        s.expect.kind === 'add-substance' || s.expect.kind === 'dip-test' || s.expect.kind === 'heat'
    )).length;
    return opening + steps;
}

export function validateChemistryPayload(payload: unknown): string[] {
    if (!isObject(payload)) return ['payload is not a chemistry setup'];
    if (!Array.isArray(payload.vessels) || payload.vessels.length === 0) return ['payload.vessels must hold at least one vessel'];
    const vessels = payload.vessels;
    const errors: string[] = [];
    if (vessels.length > CHEMISTRY_SETUP_MAX_VESSELS) errors.push(`${vessels.length} vessels; the bench holds ${CHEMISTRY_SETUP_MAX_VESSELS}`);
    vessels.forEach((v, i) => errors.push(...vesselErrors(v, i, vessels.length)));
    if (payload.steps !== undefined) {
        if (!Array.isArray(payload.steps)) errors.push('payload.steps is not a list');
        else {
            if (payload.steps.length > CHEMISTRY_SETUP_MAX_STEPS) errors.push(`${payload.steps.length} steps; at most ${CHEMISTRY_SETUP_MAX_STEPS}`);
            payload.steps.forEach((s, i) => errors.push(...stepErrors(s, i, vessels.length)));
        }
    }
    if (!optText(payload.conclusion, LONG_TEXT_MAX)) errors.push(`the conclusion is longer than ${LONG_TEXT_MAX} characters`);
    if (errors.length) return errors;

    const p = payload as unknown as ChemistrySetupPayload;
    // A tube that comes back round would pipe a gas into its own source forever;
    // the bench refuses one when it is connected, and so does this.
    for (let start = 0; start < p.vessels.length; start++) {
        const seen = new Set<number>();
        for (let at: number | undefined = start; at !== undefined; at = p.vessels[at].pipeTo) {
            if (seen.has(at)) return ['the delivery tubes run in a loop'];
            seen.add(at);
        }
    }
    const reactions = chemistryReactionCount(p);
    if (reactions > CHEMISTRY_SESSION_REACTIONS) {
        return [`a run of this asks for ${reactions} reactions, and one session allows ${CHEMISTRY_SESSION_REACTIONS} — take out some steps, or split it in two`];
    }
    return [];
}

const nameOf = (v: ChemistrySetupVessel): string => v.label?.trim() || VESSEL_LABELS[v.vesselType] || v.vesselType;
const reagent = (id: string): string => findSubstance(id)?.displayName ?? id;

/** "Guided · 6 steps · Reaction tube, Lime water", or "Test tube: Zinc, Dilute Hydrochloric Acid · Beaker". */
export function summariseChemistry(payload: ChemistrySetupPayload): string {
    const steps = payload.steps?.length ?? 0;
    const line = steps
        ? [`Guided · ${steps} step${steps > 1 ? 's' : ''}`, payload.vessels.map(nameOf).join(', ')]
        : payload.vessels.map(v => (v.contents.length ? `${nameOf(v)}: ${v.contents.map(c => reagent(c.substanceId)).join(', ')}` : nameOf(v)));
    const text = line.join(' · ');
    return text.length > 200 ? `${text.slice(0, 199)}…` : text;
}

export const chemistrySetupAdapter = {
    labId: 'chemistry-lab' as const,
    version: 1,
    maxBytes: 64 * 1024,
    validate: (payload: unknown): string[] => validateChemistryPayload(payload),
    migrate: (payload: unknown): ChemistrySetupPayload => payload as ChemistrySetupPayload,
    summary: summariseChemistry,
};
